import { useI18n } from 'vue-i18n';
import { toast as sonnerToast } from 'vue-sonner';
import lang from '~/core/lang';

export type ToastType = 'success' | 'danger' | 'warning' | 'info';

export interface ToastAction {
    label: TranslatableMessage;
    onClick: () => void;
}

export interface ToastOptions {
    type: ToastType;
    title: TranslatableMessage;
    messages?: TranslatableMessage[];
    duration?: number;
    action?: ToastAction;
}

type TranslateFunction = (key: string, params?: Record<string, unknown>) => string;
type TranslationExistsFunction = (key: string) => boolean;

/**
 * Resolves a translation key, falling back to `${key}.title` when the key
 * itself points to an object node (e.g. server.errors.installing has
 * .title/.description children but no string at the root). Without this,
 * toasts surfaced from backend error codes like `server.errors.*` render
 * the raw key because vue-i18n won't stringify an object branch.
 */
function translateKey(t: TranslateFunction, te: TranslationExistsFunction, key: string, params?: Record<string, unknown>): string {
    if (!te(key) && te(`${key}.title`)) {
        return t(`${key}.title`, params);
    }
    return t(key, params);
}

/**
 * Translates a TranslatableMessage to a string
 */
function translateWith(t: TranslateFunction, te: TranslationExistsFunction, message: TranslatableMessage): string {
    if (Array.isArray(message)) {
        if (message[0] === '_raw') return message[1];
        return translateKey(t, te, message[0], message[1]);
    }
    return translateKey(t, te, message as string);
}

/**
 * Translates a TranslatableMessage off the global instance, for the cases that
 * render an error inline (an alert on the page) instead of as a toast.
 */
export function translateMessage(message: TranslatableMessage): string {
    return translateWith(lang.global.t as TranslateFunction, lang.global.te as TranslationExistsFunction, message);
}

/**
 * Shows a toast notification (can be used outside Vue component context)
 */
function showToast(options: ToastOptions): void {
    const t = lang.global.t as TranslateFunction;
    const te = lang.global.te as TranslationExistsFunction;
    const translatedTitle = translateWith(t, te, options.title);
    const translatedMessages = options.messages?.map((m) => translateWith(t, te, m));
    const translatedAction = options.action ? { ...options.action, label: translateWith(t, te, options.action.label) } : undefined;

    const description = translatedMessages?.length ? translatedMessages.map((m) => `• ${m}`).join('\n') : undefined;

    const toastOptions = {
        description,
        duration: options.duration ?? 5000,
        action: translatedAction,
    };

    switch (options.type) {
        case 'success':
            sonnerToast.success(translatedTitle, toastOptions);
            break;
        case 'danger':
            sonnerToast.error(translatedTitle, toastOptions);
            break;
        case 'warning':
            sonnerToast.warning(translatedTitle, toastOptions);
            break;
        case 'info':
            sonnerToast.info(translatedTitle, toastOptions);
            break;
        default:
            sonnerToast(translatedTitle, toastOptions);
    }
}

/**
 * Global toast API for use outside Vue components (e.g., in stores, services)
 */
export const toast = {
    show: showToast,
    success: (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number, action?: ToastAction): void => {
        showToast({ type: 'success', title, messages, duration, action });
    },
    error: (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number): void => {
        showToast({ type: 'danger', title, messages, duration });
    },
    warning: (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number): void => {
        showToast({ type: 'warning', title, messages, duration });
    },
    info: (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number): void => {
        showToast({ type: 'info', title, messages, duration });
    },
    dismiss: (toastId?: string | number): void => {
        sonnerToast.dismiss(toastId);
    },
};

/**
 * Composable for showing toast notifications within Vue components
 */
export const useToast = () => {
    const { t, te } = useI18n();

    const show = (options: ToastOptions): void => {
        const translatedTitle = translateWith(t, te, options.title);
        const translatedMessages = options.messages?.map((m) => translateWith(t, te, m));
        const translatedAction = options.action ? { ...options.action, label: translateWith(t, te, options.action.label) } : undefined;

        const description = translatedMessages?.length ? translatedMessages.map((m) => `• ${m}`).join('\n') : undefined;

        const toastOptions = {
            description,
            duration: options.duration ?? 5000,
            action: translatedAction,
        };

        switch (options.type) {
            case 'success':
                sonnerToast.success(translatedTitle, toastOptions);
                break;
            case 'danger':
                sonnerToast.error(translatedTitle, toastOptions);
                break;
            case 'warning':
                sonnerToast.warning(translatedTitle, toastOptions);
                break;
            case 'info':
                sonnerToast.info(translatedTitle, toastOptions);
                break;
            default:
                sonnerToast(translatedTitle, toastOptions);
        }
    };

    const success = (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number, action?: ToastAction): void => {
        show({ type: 'success', title, messages, duration, action });
    };

    const error = (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number): void => {
        show({ type: 'danger', title, messages, duration });
    };

    const warning = (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number): void => {
        show({ type: 'warning', title, messages, duration });
    };

    const info = (title: TranslatableMessage, messages?: TranslatableMessage[], duration?: number): void => {
        show({ type: 'info', title, messages, duration });
    };

    const dismiss = (toastId?: string | number): void => {
        sonnerToast.dismiss(toastId);
    };

    return {
        show,
        success,
        error,
        warning,
        info,
        dismiss,
    };
};
